import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config/api';
import { 
  TrendingUp, TrendingDown, Wallet, PiggyBank, Calendar, 
  ChevronLeft, ChevronRight, Save, X, ArrowUp, ArrowDown,
  Sparkles, Target, Clock, BarChart3, Bookmark
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const PremiumMonthlyReport = ({ open, onClose, calendarId, currency = '€', initialYear, initialMonth }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(null);
  const [currentDate, setCurrentDate] = useState({ 
    year: initialYear || new Date().getFullYear(), 
    month: initialMonth || new Date().getMonth() + 1 
  });
  const [slideDirection, setSlideDirection] = useState(0);
  
  // Swipe handling
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  useEffect(() => {
    if (open && calendarId) {
      loadReport();
    }
  }, [open, calendarId, currentDate]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/premium-report/${currentDate.year}/${currentDate.month}?calendar_id=${calendarId}`,
        { withCredentials: true }
      );
      setReport(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Erreur lors du chargement du bilan');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/premium-report/${currentDate.year}/${currentDate.month}/save?calendar_id=${calendarId}`,
        {},
        { withCredentials: true }
      );
      toast.success('Bilan sauvegardé ! 📚');
      setReport(prev => ({ ...prev, is_saved: true }));
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleMonthChange = (direction) => {
    setSlideDirection(direction);
    setCurrentDate(prev => {
      let newMonth = prev.month + direction;
      let newYear = prev.year;
      if (newMonth < 1) { newMonth = 12; newYear--; }
      if (newMonth > 12) { newMonth = 1; newYear++; }
      return { year: newYear, month: newMonth };
    });
  };

  // Touch handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) handleMonthChange(1);
    if (distance < -minSwipeDistance) handleMonthChange(-1);
  };

  const formatChange = (value, isPercent = false) => {
    const sign = value >= 0 ? '+' : '';
    if (isPercent) return `${sign}${value.toFixed(0)}%`;
    return `${sign}${value.toFixed(0)}${currency}`;
  };

  const getChangeColor = (value, inverted = false) => {
    if (inverted) value = -value;
    if (value > 0) return 'text-emerald-600';
    if (value < 0) return 'text-rose-600';
    return 'text-gray-500';
  };

  if (!open) return null;

  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction < 0 ? 300 : -300, opacity: 0 })
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        
        {/* Content */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative w-full sm:max-w-lg max-h-[95vh] bg-gradient-to-b from-slate-900 to-slate-800 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-4 sticky top-0 z-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Bilan Mensuel</h2>
                  <p className="text-xs text-white/70">Analyse premium</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
            
            {/* Month Navigation */}
            <div className="flex items-center justify-between">
              <motion.button 
                onClick={() => handleMonthChange(-1)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                whileTap={{ scale: 0.9 }}
              >
                <ChevronLeft className="h-5 w-5 text-white" />
              </motion.button>
              
              <AnimatePresence mode="wait" custom={slideDirection}>
                <motion.div 
                  key={`${currentDate.month}-${currentDate.year}`}
                  custom={slideDirection}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="text-center"
                >
                  <span className="text-xl font-bold text-white">
                    {monthNames[currentDate.month - 1]} {currentDate.year}
                  </span>
                </motion.div>
              </AnimatePresence>
              
              <motion.button 
                onClick={() => handleMonthChange(1)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                whileTap={{ scale: 0.9 }}
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </motion.button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(95vh - 180px)' }}>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <motion.div 
                  className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
              </div>
            ) : report ? (
              <AnimatePresence mode="wait" custom={slideDirection}>
                <motion.div
                  key={`report-${currentDate.month}-${currentDate.year}`}
                  custom={slideDirection}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="p-4 space-y-4"
                >
                  {/* Main Stats Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <motion.div 
                      className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl p-3"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="h-4 w-4 text-emerald-400" />
                        <span className="text-xs text-emerald-300">Entrées</span>
                      </div>
                      <div className="text-xl font-bold text-white">+{report.total_credits.toFixed(0)}{currency}</div>
                      {report.comparison && (
                        <div className={`text-xs ${getChangeColor(report.comparison.credits_change)}`}>
                          {formatChange(report.comparison.credits_change)} vs mois précédent
                        </div>
                      )}
                    </motion.div>
                    
                    <motion.div 
                      className="bg-gradient-to-br from-rose-500/20 to-rose-600/10 border border-rose-500/30 rounded-xl p-3"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingDown className="h-4 w-4 text-rose-400" />
                        <span className="text-xs text-rose-300">Dépenses</span>
                      </div>
                      <div className="text-xl font-bold text-white">-{report.total_debits.toFixed(0)}{currency}</div>
                      {report.comparison && (
                        <div className={`text-xs ${getChangeColor(report.comparison.debits_change, true)}`}>
                          {formatChange(report.comparison.debits_change)} vs mois précédent
                        </div>
                      )}
                    </motion.div>
                    
                    <motion.div 
                      className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-3"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <PiggyBank className="h-4 w-4 text-purple-400" />
                        <span className="text-xs text-purple-300">Épargne</span>
                      </div>
                      <div className="text-xl font-bold text-white">+{report.total_savings.toFixed(0)}{currency}</div>
                      {report.comparison && report.comparison.savings_change !== 0 && (
                        <div className={`text-xs ${getChangeColor(report.comparison.savings_change)}`}>
                          {formatChange(report.comparison.savings_change)}
                        </div>
                      )}
                    </motion.div>
                    
                    <motion.div 
                      className={`bg-gradient-to-br ${report.end_balance >= 0 ? 'from-slate-500/20 to-slate-600/10 border-slate-500/30' : 'from-red-500/20 to-red-600/10 border-red-500/30'} border rounded-xl p-3`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Wallet className="h-4 w-4 text-slate-300" />
                        <span className="text-xs text-slate-300">Solde fin</span>
                      </div>
                      <div className="text-xl font-bold text-white">{report.end_balance.toFixed(0)}{currency}</div>
                      {report.comparison && (
                        <div className={`text-xs ${getChangeColor(report.comparison.balance_change)}`}>
                          {formatChange(report.comparison.balance_change)}
                        </div>
                      )}
                    </motion.div>
                  </div>

                  {/* Balance Evolution */}
                  <motion.div 
                    className="bg-white/5 border border-white/10 rounded-xl p-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-teal-400" />
                      Évolution du solde
                    </h3>
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <div className="text-xs text-gray-400 mb-1">Début</div>
                        <div className="text-lg font-bold text-white">{report.start_balance.toFixed(0)}{currency}</div>
                      </div>
                      <div className="flex-1 mx-4 relative">
                        <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                          <motion.div 
                            className={`h-full ${report.end_balance >= report.start_balance ? 'bg-emerald-500' : 'bg-rose-500'}`}
                            initial={{ width: 0 }}
                            animate={{ width: '100%' }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                          />
                        </div>
                        <div className={`absolute top-3 left-1/2 -translate-x-1/2 text-xs font-medium ${report.end_balance >= report.start_balance ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {report.end_balance >= report.start_balance ? (
                            <span className="flex items-center gap-1"><ArrowUp className="h-3 w-3" />{(report.end_balance - report.start_balance).toFixed(0)}{currency}</span>
                          ) : (
                            <span className="flex items-center gap-1"><ArrowDown className="h-3 w-3" />{(report.start_balance - report.end_balance).toFixed(0)}{currency}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-400 mb-1">Fin</div>
                        <div className="text-lg font-bold text-white">{report.end_balance.toFixed(0)}{currency}</div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Top Categories */}
                  {report.top_categories && report.top_categories.length > 0 && (
                    <motion.div 
                      className="bg-white/5 border border-white/10 rounded-xl p-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                    >
                      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-amber-400" />
                        Top catégories
                      </h3>
                      <div className="space-y-2">
                        {report.top_categories.slice(0, 3).map((cat, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-300 truncate">{cat.name}</span>
                              <span className="text-xs text-gray-500">({cat.transaction_count})</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
                                <motion.div 
                                  className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${cat.percentage}%` }}
                                  transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                                />
                              </div>
                              <span className="text-sm font-bold text-white w-16 text-right">{cat.amount.toFixed(0)}{currency}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* High Spending Days */}
                  {report.high_spending_days && report.high_spending_days.length > 0 && (
                    <motion.div 
                      className="bg-white/5 border border-white/10 rounded-xl p-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4 text-rose-400" />
                        Jours à forte dépense
                      </h3>
                      <div className="space-y-2">
                        {report.high_spending_days.map((day, i) => (
                          <div key={i} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0">
                            <div>
                              <span className="text-sm text-white">{new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}</span>
                              <span className="text-xs text-gray-500 ml-2">{day.main_category}</span>
                            </div>
                            <span className="text-sm font-bold text-rose-400">-{day.amount.toFixed(0)}{currency}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Projection */}
                  {report.projection && (
                    <motion.div 
                      className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 }}
                    >
                      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <Target className="h-4 w-4 text-cyan-400" />
                        Projection mois prochain
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-xs text-gray-400">Solde projeté</div>
                          <div className={`text-lg font-bold ${report.projection.projected_balance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {report.projection.projected_balance.toFixed(0)}{currency}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">Dépenses estimées</div>
                          <div className="text-lg font-bold text-white">{report.projection.projected_expenses.toFixed(0)}{currency}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-xs text-gray-400">Capacité d'épargne</div>
                          <div className="text-lg font-bold text-purple-400">{report.projection.projected_savings_capacity.toFixed(0)}{currency}</div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 italic">{report.projection.confidence_note}</p>
                    </motion.div>
                  )}

                  {/* Coach Message */}
                  <motion.div 
                    className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 border border-teal-500/30 rounded-xl p-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <p className="text-sm text-white leading-relaxed">{report.coach_message}</p>
                  </motion.div>

                  {/* Inspirational Quote */}
                  <motion.div 
                    className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-4 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55 }}
                  >
                    <Sparkles className="h-5 w-5 text-amber-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-300 italic leading-relaxed">
                      « {report.inspirational_quote} »
                    </p>
                  </motion.div>

                  {/* Save Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Button
                      onClick={handleSaveReport}
                      disabled={saving || report.is_saved}
                      className={`w-full py-6 text-base font-semibold ${
                        report.is_saved 
                          ? 'bg-gray-700 text-gray-400' 
                          : 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white'
                      }`}
                    >
                      {report.is_saved ? (
                        <>
                          <Bookmark className="h-5 w-5 mr-2" />
                          Bilan sauvegardé
                        </>
                      ) : saving ? (
                        'Sauvegarde...'
                      ) : (
                        <>
                          <Save className="h-5 w-5 mr-2" />
                          Sauvegarder ce bilan
                        </>
                      )}
                    </Button>
                  </motion.div>

                  {/* Swipe Hint */}
                  <p className="text-center text-xs text-gray-500 pb-4">
                    👆 Glissez pour changer de mois
                  </p>
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="p-8 text-center">
                <p className="text-gray-400">Aucune donnée disponible</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PremiumMonthlyReport;
